<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'fixedAsset',
    path: '/fixed-assets/:id',
    component: 'FixedAsset',
    params: [
      {
        name: 'id',
        prop: 'fixedAssetId',
      },
    ],
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'FixedAsset',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    fixedAssetId: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const item = ref(null);
  const formId = ref(null);
  const messages = ref({
    delete: $fn.value.pgettext('FixedAsset|Header', 'Delete fixed asset'),
  });
  const ux = ref({
    initialLoadSpinner: true,
  });
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_8564662037462133_parentTo = computed(() => ({
    name: 'app.fixedAssets',
  }));

  const $data = computed(() => ({
    item: item.value,
    formId: formId.value,
    messages: messages.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_8564662037462133_parentTo: b_8564662037462133_parentTo.value,
  }));

  const $$load_item = () => {
    return $fn.value.fetch('read_fixed_asset', {
      asResource: true,
      parameters: { id: props.fixedAssetId },
    });
  };
  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'item' },
      { name: 'formId' },
      { name: 'messages' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_8564662037462133_parentTo', nodeId: '8564662037462133' },
    ],
    loaders: { item: $$load_item, formId: $$load_formId },
    ux: { initialLoadSpinner: true },
    dataRefs: {
      item,
      formId,
      messages,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_8564662037462133_parentTo,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: [item.value.snapshot('name'), $fn.value.gettext('Fixed Asset')],
        icon: $config.value.fixedAsset.icon,
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.fixedAssetId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('fixedAssetId');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_f8swiv0l1_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };

  const act_7315092382398562_closeModal_f8swiv0l1 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_f8swiv0l1_getActionArgs,
    });
  const inst_evh_7315092382398562_close_j33w0z42e =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_f8swiv0l1,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_j33w0z42e],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_e6dp1ewwt =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_1h9g8bwen =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_e6dp1ewwt,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_1h9g8bwen],
    event: 'reload',
  });
  const act_4615685607825208_reloadComponentData_41px16afr =
    new actionTypes.ReloadComponentDataAction({
      displayName: 'reload',
    });
  const inst_evh_4615685607825208_clickPreventStop_d79uczxgp =
    new actionTypes.EventHandlerImpl({
      action: act_4615685607825208_reloadComponentData_41px16afr,
      event: 'click.prevent.stop',
      displayName: 'reload',
    });
  const inst_evh_4615685607825208_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_4615685607825208_clickPreventStop_d79uczxgp],
      event: 'click.prevent.stop',
    });
  const act_2095089031469157_closeComponent_3u145587d =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2095089031469157_success_vw00e4xt0 =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_closeComponent_3u145587d,
      event: 'success',
      displayName: 'closeComponent',
    });
  const inst_evh_2095089031469157_success = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2095089031469157_success_vw00e4xt0],
    event: 'success',
  });
  const act_2679161326024595_reloadComponentData_d1ubzvbgn =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_2679161326024595_reload_c1d0laenu =
    new actionTypes.EventHandlerImpl({
      action: act_2679161326024595_reloadComponentData_d1ubzvbgn,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_2679161326024595_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2679161326024595_reload_c1d0laenu],
    event: 'reload',
  });
  const act_2789477278097085_reloadComponentData_oord5e18d =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_2789477278097085_reload_xcqgxdush =
    new actionTypes.EventHandlerImpl({
      action: act_2789477278097085_reloadComponentData_oord5e18d,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_2789477278097085_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2789477278097085_reload_xcqgxdush],
    event: 'reload',
  });
  const act_8856043737157701_reloadComponentData_wdsbuzqz6 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_8856043737157701_reload_pl5w318gu =
    new actionTypes.EventHandlerImpl({
      action: act_8856043737157701_reloadComponentData_wdsbuzqz6,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_8856043737157701_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8856043737157701_reload_pl5w318gu],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_close_j33w0z42e,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_1h9g8bwen,
    inst_evh_7315092382398562_reload,
    inst_evh_4615685607825208_clickPreventStop_d79uczxgp,
    reload: inst_evh_4615685607825208_clickPreventStop_d79uczxgp,
    inst_evh_4615685607825208_clickPreventStop,
    inst_evh_2095089031469157_success_vw00e4xt0,
    inst_evh_2095089031469157_success,
    inst_evh_2679161326024595_reload_c1d0laenu,
    inst_evh_2679161326024595_reload,
    inst_evh_2789477278097085_reload_xcqgxdush,
    inst_evh_2789477278097085_reload,
    inst_evh_8856043737157701_reload_pl5w318gu,
    inst_evh_8856043737157701_reload,
  };

  const evh_7315092382398562_close_j33w0z42e = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_j33w0z42e.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_1h9g8bwen = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_1h9g8bwen.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_clickPreventStop_d79uczxgp = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_4615685607825208_clickPreventStop_d79uczxgp.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_success_vw00e4xt0 = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_success_vw00e4xt0.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_success = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_success.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2679161326024595_reload_c1d0laenu = async (event, scope) => {
    return await actions.inst_evh_2679161326024595_reload_c1d0laenu.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2679161326024595_reload = async (event, scope) => {
    return await actions.inst_evh_2679161326024595_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2789477278097085_reload_xcqgxdush = async (event, scope) => {
    return await actions.inst_evh_2789477278097085_reload_xcqgxdush.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2789477278097085_reload = async (event, scope) => {
    return await actions.inst_evh_2789477278097085_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8856043737157701_reload_pl5w318gu = async (event, scope) => {
    return await actions.inst_evh_8856043737157701_reload_pl5w318gu.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8856043737157701_reload = async (event, scope) => {
    return await actions.inst_evh_8856043737157701_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="FixedAsset"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <div class="page--v2 d-flex flex-column" v-if="loaded">
      <MaterialTabs
        :title="item.snapshot('name')"
        :parentText="gettext('Fixed Assets')"
        :parentTo="b_8564662037462133_parentTo"
        class="h-100"
      >
        <template #actions>
          <ItpButton
            variant="light"
            icon="fa-sync-alt"
            :text="gettext('Reload')"
            size="sm"
            :spinning="actions.reload.isRunning"
            @click.prevent.stop="
              evh_4615685607825208_clickPreventStop($event, {})
            "
          >
          </ItpButton>
          <BtnDeleteResource
            :resource="item"
            :dialogHeader="messages.delete"
            @success="evh_2095089031469157_success($event, {})"
          >
          </BtnDeleteResource>
        </template>
        <BTab :title="gettext('General')" class="container-lg float-start">
          <ItpRow>
            <ItpCol :w="6">
              <UcFixedAssetGeneral
                :fixedAsset="item"
                :reload="lastLoaded"
                class="pb-4"
                @reload="evh_2679161326024595_reload($event, {})"
              >
              </UcFixedAssetGeneral>
            </ItpCol>
            <ItpCol :w="6">
              <UcFixedAssetWasteBin
                :fixedAsset="item"
                :reload="lastLoaded"
                class="pb-4"
                v-if="item && item.asset_type === 'waste_bin'"
                @reload="evh_2789477278097085_reload($event, {})"
              >
              </UcFixedAssetWasteBin>
              <UcCommentsList :parent="item" :reload="lastLoaded" class="pb-4">
              </UcCommentsList>
              <UcAttachmentsList
                :parent="item"
                :reload="lastLoaded"
                class="pb-4"
              >
              </UcAttachmentsList>
              <UcFixedAssetWorkItemsPanel
                :oid="item.id"
                :reload="lastLoaded"
                class="pb-4"
                @reload="evh_8856043737157701_reload($event, {})"
              >
              </UcFixedAssetWorkItemsPanel>
            </ItpCol>
          </ItpRow>
        </BTab>
      </MaterialTabs>
    </div>
  </ItpPage>
</template>
